import { Typography } from '@mui/material';
import { Roles } from 'enums';
import PaiLink from 'features/ui/base-components/pai-link';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { Assessment } from 'types/analysis-result.types';
import PaiButton from '../../ui/base-components/pai-button';
import PaiListItem from '../../ui/base-components/pai-list-item';

interface Props {
  assessment: Assessment | undefined;
  isSignificant: boolean | undefined;

  handleOpenPersonaNameDialog(): void;

  handleOpenCreateGlobalProfileDialog(): void;
}

const CreateProfile: React.FC<Props> = ({ assessment, isSignificant, handleOpenPersonaNameDialog, handleOpenCreateGlobalProfileDialog }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { role } = useUserData();

  const motiveIllustrationGenerator = () => {
    const baseUrl = '/dashboard/images/illustrations/';

    const motive = assessment?.name || '';

    return `${baseUrl}${_.replace(_.toLower(motive), ' ', '')}.png`;
  };

  return (
    <div className={classes.container}>
      {!_.isNil(assessment) ? (
        <div className={classes.assessment}>
          {/* upper section */}
          <Typography className={classes.title} align={'center'} textTransform={'uppercase'}>
            {t('personalityType')}
          </Typography>
          <img className={classes.illustration} src={motiveIllustrationGenerator()} alt="motive illustration" />
          <div className={classes.spacer} />

          {/* middle section */}
          <Typography className={classes.title} align={'center'}>
            {assessment?.instructions?.descriptions?.shortdescription}
          </Typography>
          {assessment?.instructions?.descriptions?.listdescription?.map((item, index) => (
            <PaiListItem key={index} text={item} />
          ))}

          <div className={classes.spacer} />

          {/* bottom section */}
          <Typography className={classes.title} textTransform={'uppercase'}>
            {t('importantToContact', { contactName: assessment.name || t('theContact') })}
          </Typography>
          {assessment.instructions?.descriptions?.importantdescription?.map((item, index) => (
            <PaiListItem key={index} text={item} />
          ))}
        </div>
      ) : null}

      <div className={classes.spacer} />
      <Typography className={classes.significance}>{isSignificant ? t('personaSignificantText') : t('personaNotSignificantText')}</Typography>

      <div className={classes.buttonContainer}>
        {isSignificant ? (
          <PaiButton text={t('savePersona')} action={handleOpenPersonaNameDialog} />
        ) : (
          <PaiLink text={t('savePersonaAnyway')} size={'sm'} underline={'always'} action={handleOpenPersonaNameDialog} />
        )}
      </div>

      {role === Roles.ADMIN ? (
        <>
          <div className={classes.spacer} />
          <div className={classes.buttonContainer}>
            <PaiLink text={t('globallySavePersona')} size={'sm'} underline={'always'} action={handleOpenCreateGlobalProfileDialog} />
          </div>
        </>
      ) : null}
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    width: '100%',
    border: `2px solid ${theme.palette.backgrounds.main}`,
    padding: '20px 16px',
    height: 'calc(100% - 39px - 16px - 20px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  assessment: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  illustration: {
    width: 'auto',
    maxHeight: '150px',
  },
  title: {
    width: '100%',
    fontSize: '12px',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    marginBottom: '16px',
  },
  spacer: {
    height: '16px',
  },
  significance: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.error.main,
    maxWidth: '300px',
    textAlign: 'center',
    marginBottom: '24px',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export default CreateProfile;
